import { NvTopbar } from '@simwood/nv-react-components-v2';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AudioDeviceModal from '../components/AudioDeviceModal';
import CallHistoryList from '../components/CallHistoryList';
import CenterColumn from '../components/CenterColumn';
import ContactList from '../components/ContactList';
import ExtensionSelect from '../components/ExtensionSelect';
import { logActivity } from '../helpers/activityLog';
import { getAudioDevices, testCanChangeAudioDevice, } from '../helpers/getAudioDevices';
import { isWeb } from '../helpers/platform';
import * as platform from '../helpers/platformApi';
import { getInboundRinging, outboundRinging, testAudio, } from '../helpers/ringing';
import nvStorage from '../helpers/storage';
import { fetchCallHistory, fetchNewCalls } from '../reducers/callHistory';
import { grantAudio, selectAudioInputDevice, selectAudioOutputDevice, selectRingingOutputDevice, } from '../reducers/client';
import { fetchContacts } from '../reducers/contacts';
import { registerExtension } from '../reducers/extension';
import { logout } from '../reducers/user';
import styles from '../styles/client.scss';
import Hammer from './Hammer';
import Popover from './Popover';
import { ChevronLeftIcon, ChevronRightIcon, ClearIcon } from './icons';
// TODO move
platform.init();
const Client = () => {
    const dispatch = useDispatch();
    const newOutboundCall = useSelector((state) => !!state.calls.find((c) => c.direction === 'OUT' && c.ringing));
    const isRegistered = useSelector((state) => state.extension.isRegistered);
    const isRegistering = useSelector((state) => state.extension.isRegistering);
    const isUnregistering = useSelector((state) => state.extension.isUnregistering);
    const extension = useSelector((state) => state.extension.extension);
    const activeCustomer = useSelector((state) => state.client.activeCustomer);
    const linkedUser = useSelector((state) => state.client.linkedUser);
    const favicons = useSelector((state) => state.branding.favicons);
    const calls = useSelector((state) => state.calls);
    const apps = useSelector((state) => state.branding.apps);
    const topBarInverted = useSelector((state) => { var _a; return (_a = state.branding.branding) === null || _a === void 0 ? void 0 : _a.topbarInverted; });
    const kbLinkLocation = useSelector((state) => state.branding.kbLinkLocation);
    const logoSrc = useSelector((state) => state.branding.appLogoSrc);
    const selectedAudioInputDeviceId = useSelector((state) => state.client.selectedAudioInputDeviceId);
    const selectedAudioOutputDeviceId = useSelector((state) => state.client.selectedAudioOutputDeviceId);
    const selectedRingingOutputDeviceId = useSelector((state) => state.client.selectedRingingOutputDeviceId);
    const hasGrantedAudio = useSelector((state) => state.client.hasGrantedAudio);
    const lastOpenPreferencesRequest = useSelector((state) => state.client.lastOpenPreferencesRequest);
    const icon32 = ((favicons &&
        favicons.find((fi) => fi.name.toLowerCase() === 'favicon-32x32.png')) ||
        {}).location;
    const doFetchContacts = () => {
        dispatch(fetchContacts());
    };
    const doFetchCallHistory = (overrideParams) => {
        dispatch(fetchCallHistory({ overrideParams }));
    };
    const doFetchNewCalls = () => {
        dispatch(fetchNewCalls());
    };
    const doRegisterExtension = (username, password, domain, audio) => {
        dispatch(registerExtension({ username, password, domain, audio }));
    };
    const doLogout = () => {
        dispatch(logout());
    };
    const doSelectAudioInputDevice = (deviceId) => {
        dispatch(selectAudioInputDevice(deviceId));
    };
    const doSelectAudioOutputDevice = (deviceId) => {
        dispatch(selectAudioOutputDevice(deviceId));
    };
    const doSelectRingingOutputDevice = (deviceId) => {
        dispatch(selectRingingOutputDevice(deviceId));
    };
    const doGrantAudio = (hasGrantedAudio) => {
        dispatch(grantAudio(hasGrantedAudio));
    };
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);
    const [audioDeviceModalVisible, setAudioDeviceModalVisible] = useState(false);
    const [hasRegisteredOnce, setHasRegisteredOnce] = useState(false);
    const [canChangeAudioDevice] = useState(testCanChangeAudioDevice());
    const [requirePermissionsPrompt, setRequirePermissionsPrompt] = useState(false);
    const [autoplayFailed, setAutoplayFailed] = useState(false);
    const localAudioRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const prevPropsRef = useRef({
        extension,
        hasGrantedAudio,
        selectedAudioInputDeviceId,
        selectedAudioOutputDeviceId,
        selectedRingingOutputDeviceId,
        isRegistered,
        newOutboundCall,
    });
    const lastOpenPreferencesRequestRef = useRef();
    const checkAndRegisterExtension = (reason, force) => {
        const localAudio = localAudioRef.current;
        const remoteAudio = remoteAudioRef.current;
        const audio = {
            local: localAudio,
            remote: remoteAudio,
        };
        if (!localAudio || !remoteAudio) {
            return;
        }
        if ((extension === null || extension === void 0 ? void 0 : extension.hasOwnProperty('sip')) &&
            !isRegistering &&
            !isUnregistering) {
            if (!isRegistered || force) {
                const { username, password, domain } = extension.sip;
                console.debug(`Registering extension ${username}`);
                logActivity('default', 'REGISTER_EXTENSION', {
                    reason,
                    force: !!force,
                });
                doRegisterExtension(username, password, domain, audio);
            }
        }
    };
    const getMedia = () => {
        const constraints = {
            audio: true,
            video: false,
        };
        // request userMedia
        navigator.mediaDevices.getUserMedia(constraints).then(() => {
            doGrantAudio(true);
            checkAndRegisterExtension('MEDIA_DEVICES');
        }, (err) => {
            console.error(err);
        });
        // track changes in audio permissions
        // TODO this method is not supported in Firefox. Doesn't seem to
        // have any consequences, but double check just to be sure.
        navigator.permissions
            .query({ name: 'microphone' })
            .then((audioPermission) => {
            audioPermission.onchange = () => {
                if (audioPermission.state === 'granted') {
                    doGrantAudio(true);
                    checkAndRegisterExtension('MIC_PERMISSION');
                }
                if (audioPermission.state === 'denied') {
                    doGrantAudio(false);
                }
                if (audioPermission.state === 'prompt') {
                    doGrantAudio(false);
                    setRequirePermissionsPrompt(true);
                }
            };
        })
            .catch((err) => {
            console.warn('Cannot query permissions API', err);
        });
    };
    // Attach audio output device to element using device/sink ID.
    // TODO setSinkId doesn't exist on media elements in Firefox,
    // so only the default devices can be used.
    const attachSinkId = (element, device, deviceType = '') => {
        var _a;
        if (typeof element.sinkId !== 'undefined') {
            (_a = element
                .setSinkId) === null || _a === void 0 ? void 0 : _a.call(element, device.deviceId).then(() => {
                console.debug(`Success (${deviceType}): ${device.label}`);
            }).catch((error) => {
                let errorMessage = error;
                if (error.name === 'SecurityError') {
                    errorMessage = `You need to use HTTPS for selecting audio device: ${error}`;
                }
                console.error(deviceType, errorMessage);
            });
        }
        else {
            console.warn('Browser does not support device selection.');
        }
    };
    const setAudioDevices = () => {
        if (canChangeAudioDevice) {
            const localAudio = localAudioRef.current;
            const remoteAudio = remoteAudioRef.current;
            getAudioDevices({
                selectedAudioInputDeviceId,
                selectedAudioOutputDeviceId,
                selectedRingingOutputDeviceId,
            })
                .then((devices) => {
                const { selectedAudioInputDevice, selectedAudioOutputDevice, selectedRingingOutputDevice, } = devices;
                if (localAudio && selectedAudioInputDevice)
                    attachSinkId(localAudio, selectedAudioInputDevice, 'Microphone');
                if (remoteAudio && selectedAudioOutputDevice)
                    attachSinkId(remoteAudio, selectedAudioOutputDevice, 'Remote Audio');
                if (outboundRinging && selectedAudioOutputDevice)
                    attachSinkId(outboundRinging, selectedAudioOutputDevice, 'Outbound Ringing');
                const inboundRinging = getInboundRinging();
                if (inboundRinging && selectedRingingOutputDevice)
                    attachSinkId(inboundRinging, selectedRingingOutputDevice, 'Inbound Ringing');
            })
                .catch(console.error);
        }
    };
    useEffect(() => {
        getMedia();
        doFetchContacts();
        doFetchCallHistory();
        const refreshLoop = setInterval(() => {
            doFetchContacts();
            doFetchNewCalls();
        }, 60000);
        if (canChangeAudioDevice) {
            const selectedAudioInputDeviceId = nvStorage.get('selectedAudioInputDeviceId');
            const selectedAudioOutputDeviceId = nvStorage.get('selectedAudioOutputDeviceId');
            const selectedRingingOutputDeviceId = nvStorage.get('selectedRingingOutputDeviceId');
            if (selectedAudioInputDeviceId)
                doSelectAudioInputDevice(selectedAudioInputDeviceId);
            if (selectedAudioOutputDeviceId)
                doSelectAudioOutputDevice(selectedAudioOutputDeviceId);
            if (selectedRingingOutputDeviceId)
                doSelectRingingOutputDevice(selectedRingingOutputDeviceId);
            setAudioDevices();
            navigator.mediaDevices.ondevicechange = () => setAudioDevices();
        }
        platform.requestNotificationPermissions();
        // check if autoplay is enabled
        testAudio
            .play()
            .then(() => {
            testAudio.pause();
        })
            .catch(() => {
            console.debug('Autoplay failed');
            setAutoplayFailed(true);
        });
        return () => {
            if (refreshLoop) {
                clearInterval(refreshLoop);
            }
        };
    }, []);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        const thisExtn = extension;
        const prevExtn = (_a = prevPropsRef.current) === null || _a === void 0 ? void 0 : _a.extension;
        let force = false;
        if ((thisExtn === null || thisExtn === void 0 ? void 0 : thisExtn.sip) &&
            (prevExtn === null || prevExtn === void 0 ? void 0 : prevExtn.sip) &&
            thisExtn.sip.username !== prevExtn.sip.username) {
            logActivity('default', 'SIP_USERNAME_CHANGED', {
                new: thisExtn.sip.username,
                prev: prevExtn.sip.username,
            });
            force = true;
        }
        if (hasGrantedAudio && !((_b = prevPropsRef.current) === null || _b === void 0 ? void 0 : _b.hasGrantedAudio)) {
            checkAndRegisterExtension('HAS_GRANTED_AUDIO', force);
        }
        if (((_c = prevPropsRef.current) === null || _c === void 0 ? void 0 : _c.selectedAudioInputDeviceId) !==
            selectedAudioInputDeviceId ||
            ((_d = prevPropsRef.current) === null || _d === void 0 ? void 0 : _d.selectedAudioOutputDeviceId) !==
                selectedAudioOutputDeviceId ||
            ((_e = prevPropsRef.current) === null || _e === void 0 ? void 0 : _e.selectedRingingOutputDeviceId) !==
                selectedRingingOutputDeviceId) {
            setAudioDevices();
        }
        // -----------------------------------------
        if (hasRegisteredOnce &&
            !isRegistered &&
            ((_f = prevPropsRef.current) === null || _f === void 0 ? void 0 : _f.isRegistered)) {
            platform.notify(undefined, 'Communicator lost connection', '', {
                icon: icon32,
                timeout: 0,
            });
        }
        if (newOutboundCall && !((_g = prevPropsRef.current) === null || _g === void 0 ? void 0 : _g.newOutboundCall)) {
            hideAll();
        }
        if (!hasRegisteredOnce && isRegistered) {
            setHasRegisteredOnce(true);
        }
        if (lastOpenPreferencesRequest &&
            lastOpenPreferencesRequest !== lastOpenPreferencesRequestRef.current) {
            lastOpenPreferencesRequestRef.current = lastOpenPreferencesRequest;
            setAudioDeviceModalVisible(true);
        }
        prevPropsRef.current = {
            extension,
            hasGrantedAudio,
            selectedAudioInputDeviceId,
            selectedAudioOutputDeviceId,
            selectedRingingOutputDeviceId,
            isRegistered,
            newOutboundCall,
        };
    });
    const hideAll = () => {
        setShowLeft(false);
        setShowRight(false);
    };
    const doShowLeft = () => {
        setShowLeft(true);
        setShowRight(false);
    };
    const doShowRight = () => {
        setShowLeft(false);
        setShowRight(true);
    };
    const handleCenterSwipe = (e) => {
        const LEFT = 2;
        const RIGHT = 4;
        const { direction } = e;
        if (direction === LEFT) {
            if (showLeft) {
                // Left is showing and we swipe it away
                hideAll();
            }
            else if (showRight) {
                // Right is showing and we swipe to open it again
                // Do nothing
            }
            else {
                // Both are hidden and we swipe left
                doShowRight();
            }
        }
        else if (direction === RIGHT) {
            if (showLeft) {
                // Left is showing and we swipe to open it again
                // Do nothing
            }
            else if (showRight) {
                // Right is showing and we swipe it away
                hideAll();
            }
            else {
                // Both are hidden and we swipe right
                // Show left
                doShowLeft();
            }
        }
    };
    const handleSidebarSwipe = (e) => {
        const LEFT = 2;
        const RIGHT = 4;
        const { direction } = e;
        if (showLeft && direction === LEFT) {
            // We're swiping the left drawer away
            hideAll();
        }
        else if (showRight && direction === RIGHT) {
            // We're swiping the right drawer away
            hideAll();
        }
    };
    const handleTap = (side) => {
        if (side === 'LEFT') {
            if (showLeft) {
                hideAll();
            }
            else {
                doShowLeft();
            }
        }
        else if (side === 'RIGHT') {
            if (showRight) {
                hideAll();
            }
            else {
                doShowRight();
            }
        }
    };
    const overlayTap = (e) => {
        e.preventDefault();
        hideAll();
    };
    outboundRinging.loop = true;
    const inboundRinging = getInboundRinging();
    inboundRinging.loop = true;
    if (!(extension === null || extension === void 0 ? void 0 : extension.id)) {
        return React.createElement(ExtensionSelect, null);
    }
    const ringingCall = calls.find((c) => c.ringing && !c.forceStopRinging);
    const activeOngoingCall = calls.find((c) => c.active && c.answered);
    if (ringingCall && !activeOngoingCall) {
        if (ringingCall.direction === 'OUT') {
            outboundRinging.play();
        }
        else if (ringingCall.direction === 'IN') {
            inboundRinging.play();
        }
    }
    else {
        if (!outboundRinging.paused) {
            outboundRinging.pause();
            outboundRinging.currentTime = 0;
        }
        if (!inboundRinging.paused) {
            inboundRinging.pause();
            inboundRinging.currentTime = 0;
        }
    }
    const userMenuItems = [
        {
            label: 'Log out',
            onClick: () => {
                doLogout();
            },
        },
    ];
    let settingsMenuItems = [];
    if (kbLinkLocation) {
        settingsMenuItems.push({
            label: 'Knowledge Base',
            href: kbLinkLocation,
            target: '_blank',
        });
    }
    if (canChangeAudioDevice) {
        settingsMenuItems = [
            {
                label: 'Audio Settings',
                onClick: () => {
                    setAudioDeviceModalVisible(true);
                },
            },
            ...settingsMenuItems,
        ];
    }
    const mappedApps = apps === null || apps === void 0 ? void 0 : apps.map((app) => {
        return {
            label: app.name,
            icon: app.icon,
            href: app.url,
        };
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.container },
            isWeb() && (React.createElement(NvTopbar, { className: styles.top_bar, customerName: activeCustomer === null || activeCustomer === void 0 ? void 0 : activeCustomer.company, product: 'Communicator', userEmail: linkedUser === null || linkedUser === void 0 ? void 0 : linkedUser.email, apps: isWeb() ? mappedApps : undefined, userMenuItems: userMenuItems, settingsMenuItems: settingsMenuItems, brandSrc: logoSrc, showBeta: false, inverted: topBarInverted })),
            React.createElement("div", { className: isWeb() ? styles.content : styles.contentFullHeight },
                showLeft || showRight ? (React.createElement(Hammer, { onTap: overlayTap },
                    React.createElement("div", { className: styles.close_area }))) : (''),
                React.createElement("div", { className: showLeft ? styles.left_visible : styles.left_hidden },
                    React.createElement(Hammer, { onSwipe: handleSidebarSwipe },
                        React.createElement("div", { className: styles.left_sidebar_container },
                            React.createElement(ContactList, null))),
                    React.createElement(Hammer, { onSwipe: () => handleTap('LEFT'), onTap: () => handleTap('LEFT') },
                        React.createElement("div", { className: showLeft ? styles.left_handle_open : styles.left_handle_closed },
                            React.createElement(ChevronRightIcon, null)))),
                React.createElement("div", { className: styles.column__middle },
                    !hasGrantedAudio && (React.createElement("span", { className: styles.audio_permissions_denied }, requirePermissionsPrompt
                        ? 'Permissions have been reset, please refresh the page for new settings to take effect'
                        : `Looks like we don't have access to your microphone. You won't be able to make or receive calls until this is enabled.`)),
                    React.createElement(Hammer, { onSwipe: handleCenterSwipe },
                        React.createElement("div", { className: styles.center_column },
                            React.createElement(CenterColumn, null)))),
                React.createElement("div", { className: showRight ? styles.right_visible : styles.right_hidden },
                    React.createElement(Hammer, { onSwipe: handleSidebarSwipe },
                        React.createElement("div", { className: styles.right_sidebar_container },
                            React.createElement(CallHistoryList, null))),
                    React.createElement(Hammer, { onSwipe: () => handleTap('RIGHT'), onTap: () => handleTap('RIGHT') },
                        React.createElement("div", { className: showRight
                                ? styles.right_handle_open
                                : styles.right_handle_closed },
                            React.createElement(ChevronLeftIcon, null)))),
                React.createElement("audio", { autoPlay: true, ref: localAudioRef, muted: true }),
                React.createElement("audio", { autoPlay: true, ref: remoteAudioRef }),
                hasGrantedAudio &&
                    canChangeAudioDevice &&
                    audioDeviceModalVisible && (React.createElement(AudioDeviceModal, { onCancel: () => {
                        setAudioDeviceModalVisible(false);
                    }, onSave: () => {
                        setAudioDeviceModalVisible(false);
                    } }))),
            hasRegisteredOnce && !isRegistered && (React.createElement(Popover, { title: 'Connection Lost', message: "Looks like we're having some trouble connecting, hang on a moment and we'll keep trying." }))),
        autoplayFailed && (React.createElement("div", { className: styles.autoplayModalContainer },
            React.createElement("div", { className: styles.autoplayModal },
                React.createElement("span", null, "Please close this modal to enable ringing sound to be played"),
                React.createElement("div", { className: styles.autoplayModalCloseButton, onClick: () => {
                        setAutoplayFailed(false);
                    } },
                    React.createElement(ClearIcon, { size: '1.3em' })))))));
};
export default Client;
